/**
 * PhoneField - поле телефона с маской +7 000 000 00 00.
 *
 * Отдает номер в виде маски (как formatPhoneWithSpaces), национальная часть - до 10 цифр после +7.
 * Курсор сохраняется по номеру цифры: ввод и удаление в середине номера не перескакивают в конец,
 * Backspace/Delete на пробеле удаляют соседнюю цифру, префикс +7 не редактируется,
 * в полный номер лишняя цифра не добавляется (последняя не вытесняется).
 * Вставка из буфера принимает любое написание: «+7 701 …», «8 701 …», «7011234567»; так же разбирается
 * номер, пришедший в поле целиком (подсказка клавиатуры телефона, перетаскивание, голосовой ввод).
 */
import React from 'react';
import { TextField } from '@mui/material';
import { formatNationalPhone, formatPhoneWithSpaces, phoneNationalDigits, PHONE_NATIONAL_DIGITS } from './utils';

const PREFIX = '+7 ';

export interface PhoneFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

/** Национальные цифры в маске до позиции position */
const digitsBefore = (masked: string, position: number): number =>
  masked.slice(PREFIX.length, Math.max(position, PREFIX.length)).replace(/\D/g, '').length;

/** Позиция курсора в маске после count национальных цифр */
const positionAfter = (national: string, count: number): number =>
  count <= 0 ? PREFIX.length : formatNationalPhone(national.slice(0, count)).length;

/** Длина общего начала двух строк */
const commonPrefixLength = (a: string, b: string): number => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
};

/** Национальные цифры вставленного текста: «+7 …» - по правилам сервера, 11 цифр с 7/8 в начале - без кода страны */
const pastedNational = (text: string): string => {
  if (text.trim().startsWith('+')) return phoneNationalDigits(text);
  const digits = text.replace(/\D/g, '');
  const withCountryCode = digits.length === PHONE_NATIONAL_DIGITS + 1 && (digits[0] === '7' || digits[0] === '8');
  return withCountryCode ? digits.slice(1) : digits;
};

const PhoneField: React.FC<PhoneFieldProps> = ({
  value,
  onChange,
  size = 'small',
  fullWidth = true,
  placeholder = '+7 000 000 00 00',
  ...rest
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const display = formatPhoneWithSpaces(value);
  const national = phoneNationalDigits(display);

  // Курсор ставится после перерисовки: React заменяет значение поля и сдвигает курсор в конец
  const placeCaret = (position: number) => {
    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input && document.activeElement === input) input.setSelectionRange(position, position);
    });
  };

  const commit = (next: string, caretDigits: number) => {
    onChange(formatNationalPhone(next));
    placeCaret(positionAfter(next, caretDigits));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart === null ? raw.length : e.target.selectionStart;
    if (!raw.startsWith(PREFIX)) {
      // Префикс заменен вместе с номером: выделить все и ввести, автозаполнение браузера
      const next = phoneNationalDigits(raw);
      commit(next, next.length);
      return;
    }
    const digits = raw.slice(PREFIX.length).replace(/\D/g, '');
    const before = digitsBefore(raw, caret);
    if (digits.length > PHONE_NATIONAL_DIGITS) {
      // Несколько цифр за одно изменение - разбор как у вставки из буфера, иначе номер целиком отбрасывается
      const from = Math.max(PREFIX.length, Math.min(commonPrefixLength(display, raw), caret - Math.max(0, raw.length - display.length)));
      const inserted = raw.slice(from, caret).replace(/\D/g, '');
      if (inserted.length > 1) {
        const head = digitsBefore(raw, from);
        const added = pastedNational(inserted);
        const next = (digits.slice(0, head) + added + digits.slice(before)).slice(0, PHONE_NATIONAL_DIGITS);
        commit(next, Math.min(head + added.length, next.length));
        return;
      }
      commit(national, Math.max(0, before - (digits.length - national.length)));
      return;
    }
    commit(digits, before);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const start = input.selectionStart === null ? input.value.length : input.selectionStart;
    const end = input.selectionEnd === null ? start : input.selectionEnd;
    if (start !== end) return; // удаление и замену выделенного разбирает handleChange

    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const index = digitsBefore(input.value, start) - (e.key === 'Backspace' ? 1 : 0);
      if (index < 0 || index >= national.length) return;
      commit(national.slice(0, index) + national.slice(index + 1), index);
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && start < PREFIX.length) {
      input.setSelectionRange(PREFIX.length, PREFIX.length);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const input = e.currentTarget;
    const start = input.selectionStart === null ? input.value.length : input.selectionStart;
    const end = input.selectionEnd === null ? start : input.selectionEnd;
    const from = digitsBefore(input.value, start);
    const to = digitsBefore(input.value, end);
    const inserted = pastedNational(e.clipboardData.getData('text'));
    const next = (national.slice(0, from) + inserted + national.slice(to)).slice(0, PHONE_NATIONAL_DIGITS);
    commit(next, Math.min(from + inserted.length, next.length));
  };

  const handleFocus = () => {
    if (display) return;
    onChange(PREFIX);
    placeCaret(PREFIX.length);
  };

  const handleBlur = () => {
    if (!national && value) onChange('');
  };

  return (
    <TextField
      {...rest}
      type="tel"
      size={size}
      fullWidth={fullWidth}
      placeholder={placeholder}
      value={display}
      inputRef={inputRef}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{ inputMode: 'tel', autoComplete: 'tel', onKeyDown: handleKeyDown, onPaste: handlePaste }}
    />
  );
};

export default PhoneField;
